import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format, parseISO } from 'date-fns';
import { getClinics } from '../services/adminService';

const LogsManager = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [fromDate, setFromDate] = useState(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd')); // 30 days ago
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [detailDialog, setDetailDialog] = useState({ open: false, data: null, type: null });

  // Load clinics on component mount
  useEffect(() => {
    loadClinics();
  }, []);

  const loadClinics = async () => {
    try {
      const response = await getClinics();
      // Handle the response structure - getClinics returns { clinics: [...] }
      const clinicsData = response.clinics || response || [];
      setClinics(clinicsData);
      if (clinicsData.length > 0) {
        setSelectedClinic(clinicsData[0].id);
      }
    } catch (error) {
      console.error('Error loading clinics:', error);
      setError('Failed to load clinics');
    }
  };

  const fetchData = async () => {
    if (!selectedClinic) {
      setError('Please select a clinic');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dateParams = {
        clinic_id: selectedClinic,
        from_date: fromDate,
        to_date: toDate,
        limit: 100
      };

      // Fetch all data in parallel
      const [appointmentsRes, paymentsRes, summaryRes] = await Promise.all([
        fetch(`/api/admin/logs/appointments?${new URLSearchParams(dateParams)}`),
        fetch(`/api/admin/logs/payments?${new URLSearchParams(dateParams)}`),
        fetch(`/api/admin/logs/summary?${new URLSearchParams(dateParams)}`)
      ]);

      if (!appointmentsRes.ok) throw new Error('Failed to fetch appointments');
      if (!paymentsRes.ok) throw new Error('Failed to fetch payments');
      if (!summaryRes.ok) throw new Error('Failed to fetch summary');

      const appointmentsData = await appointmentsRes.json();
      const paymentsData = await paymentsRes.json();
      const summaryData = await summaryRes.json();

      setAppointments(appointmentsData.appointments || []);
      setPayments(paymentsData.payments || []);
      setSummary(summaryData.summary || {});

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when clinic or dates change
  useEffect(() => {
    if (selectedClinic) {
      fetchData();
    }
  }, [selectedClinic]);

  const handleRefresh = () => {
    fetchData();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const showDetails = (data, type) => {
    setDetailDialog({ open: true, data, type });
  };

  const renderSummaryCards = () => {
    if (!summary) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="h6">
                Total Appointments
              </Typography>
              <Typography variant="h4">
                {summary.total_appointments || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="h6">
                Paid Appointments
              </Typography>
              <Typography variant="h4" color="primary">
                {summary.paid_appointments || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="h6">
                Total Revenue
              </Typography>
              <Typography variant="h4" color="success.main">
                {formatCurrency(summary.total_revenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="h6">
                Avg. Value
              </Typography>
              <Typography variant="h4">
                {formatCurrency(summary.average_appointment_value)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAppointmentsTab = () => (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Practitioner</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments.map((appointment) => (
              <TableRow key={appointment.id}>
                <TableCell>{appointment.id}</TableCell>
                <TableCell>
                  <div>
                    <div>{appointment.patient?.first_name} {appointment.patient?.last_name}</div>
                    <Typography variant="caption" color="textSecondary">
                      {appointment.patient?.email}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>{appointment.appointment_type?.name || 'N/A'}</TableCell>
                <TableCell>{appointment.practitioner?.name || 'N/A'}</TableCell>
                <TableCell>{formatDateTime(appointment.starts_at)}</TableCell>
                <TableCell>
                  <Chip 
                    label={appointment.appointment_status || 'Unknown'} 
                    size="small" 
                    color={appointment.appointment_status === 'confirmed' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => showDetails(appointment, 'appointment')}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {appointments.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    No appointments found for the selected period
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderPaymentsTab = () => (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Processor</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {payment.transaction_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <div>
                    <div>{payment.patient_name}</div>
                    <Typography variant="caption" color="textSecondary">
                      {payment.patient_email}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell>{payment.appointment_type || 'N/A'}</TableCell>
                <TableCell>{formatCurrency(payment.amount)}</TableCell>
                <TableCell>
                  <Chip 
                    label={payment.processor} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{formatDateTime(payment.starts_at)}</TableCell>
                <TableCell>
                  <Chip 
                    label={payment.status} 
                    size="small" 
                    color={payment.status === 'completed' ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => showDetails(payment, 'payment')}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">
                    No payments found for the selected period
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderDetailDialog = () => {
    const { open, data, type } = detailDialog;
    
    return (
      <Dialog 
        open={open} 
        onClose={() => setDetailDialog({ open: false, data: null, type: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {type === 'appointment' ? 'Appointment Details' : 'Payment Details'}
        </DialogTitle>
        <DialogContent>
          {data && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {Object.entries(data).map(([key, value]) => {
                  if (typeof value === 'object' && value !== null) {
                    return (
                      <Grid item xs={12} key={key}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">{key}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <pre>{JSON.stringify(value, null, 2)}</pre>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    );
                  }
                  
                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Typography variant="subtitle2" color="textSecondary">
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2">
                        {value || 'N/A'}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, data: null, type: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Appointments & Payments Logs
      </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Clinic</InputLabel>
                <Select
                  value={selectedClinic}
                  onChange={(e) => setSelectedClinic(e.target.value)}
                  label="Clinic"
                >
                  {clinics.map((clinic) => (
                    <MenuItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Tabs */}
        <Paper sx={{ mt: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label={`Appointments (${appointments.length})`} />
            <Tab label={`Payments (${payments.length})`} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 && renderAppointmentsTab()}
            {activeTab === 1 && renderPaymentsTab()}
          </Box>
        </Paper>

        {/* Detail Dialog */}
        {renderDetailDialog()}
      </Box>
  );
};

export default LogsManager;